// Licensing shop: types, API and hooks (/api/shop/*).
import { useQuery } from '@tanstack/react-query'
import type { Detail } from './api'
import { call } from './api'

export type DimValue = { label: string; display: string; open: boolean }
export type Dim = { key: string; label: string; kind: 'tier' | 'category'; unit: string | null; values: DimValue[] }
export type Modifier = {
  code: string; label: string; kind: 'surcharge' | 'discount' | 'by_agreement'; pct: number | null; pct_max: number | null
  is_minimum: boolean; per_unit: string | null; group: string | null; requires: string | null; global: boolean
}
export type PriceTable = { code: string; title: string; note: string | null; beyond: string | null; dims: Dim[]; modifiers: Modifier[] }
export type Medium = { code: string; title: string; description: string; terms: string[]; tables: PriceTable[] }
export type UsageClass = { code: string; label: string; description: string; media: Medium[] }
export type Catalog = { classes: UsageClass[]; general_terms: string[] }

export type LicenseConfig = {
  medium: string
  table: string
  params: Record<string, string>
  modifiers: { code: string; units: number }[]
}

export type PriceLine = { label: string; pct: number | null; cents: number }
export type Priced = {
  by_agreement: boolean
  base_cents: number | null
  lines: PriceLine[]
  fee_cents: number
  fee_label: string
  license_cents?: number
  net_cents: number | null
  notes: string[]
  config: LicenseConfig
  description: string[]
  alternatives?: Record<string, Record<string, number | null>>
  complete?: boolean
  missing?: string[]
  from_cents?: number | null
}

export type ShopConfig = {
  enabled: boolean; stripe: boolean; transfer: boolean; vat_rate: number; fee_label: string; fee_cents: number
  terms_url: string; privacy_url: string; download_days: number; seller: string
}

export type AssetSnap = { inv: string; title: string; date: string; photographer: string }
export type CartRow = { id: number; asset_id: number; asset: AssetSnap; config: LicenseConfig; thumb: string; price: Priced | null; error: string | null }
export type Cart = {
  items: CartRow[]; count: number; problems: number; net_cents: number; vat_rate: number; vat_cents: number; gross_cents: number
  checkout_ready: boolean
}

export type Billing = {
  first_name: string; last_name: string; organisation: string; street: string; postal_code: string; city: string
  country: string; vat_id: string; phone: string; email: string
}

export type OrderItem = {
  id: number; position: number; asset_id: number; asset: AssetSnap; description: string[]; net_cents: number; thumb: string
  pricing: { base_cents: number; lines: PriceLine[]; fee_cents: number; fee_label: string }
  downloads: number; download_url: string | null; download_blocked: string | null
}
export type ProofKind = 'url' | 'book' | 'other'
export type Proof = {
  id: number; kind: ProofKind; kind_label: string; created_at: string; item_id: number | null
  url: string; title: string; author: string; isbn: string; text: string
  file_name: string; file_size: number; file_url: string | null; reviewed: boolean
}
export type Order = {
  number: string; status: 'awaiting_payment' | 'paid' | 'cancelled'; status_label: string; created_at: string
  billing: Billing; reference: string; net_cents: number; vat_rate: number; vat_cents: number; gross_cents: number
  invoice_number: string; invoice_date: string; due_date: string; paid_at: string | null; payment_method: string; payment_label: string
  invoice_url: string; cancellation_url: string | null
  bank: { holder: string; iban: string; bic: string; bank: string; reference: string } | null
  stripe: boolean; download_days: number; items: OrderItem[]
  proofs: Proof[]; proofs_blocked: string | null
}
export type OrderSummary = { number: string; status: Order['status']; status_label: string; created_at: string; gross_cents: number; invoice_number: string; count: number }

export type LicenseAsset = Detail & { can_license: boolean; license_blocked: string | null }

export const shop = {
  config: () => call<ShopConfig>('/shop/config'),
  catalog: () => call<Catalog>('/shop/catalog'),
  quote: (config: LicenseConfig, signal?: AbortSignal) => call<Priced>('/shop/quote', { method: 'POST', json: { config }, signal }),
  asset: (id: number) => call<LicenseAsset>(`/shop/assets/${id}`),
  cart: () => call<Cart>('/shop/cart'),
  addToCart: (asset_id: number, config: LicenseConfig) => call<Cart>('/shop/cart', { method: 'POST', json: { asset_id, config } }),
  updateCart: (id: number, config: LicenseConfig) => call<Cart>(`/shop/cart/${id}`, { method: 'PUT', json: { config } }),
  removeFromCart: (id: number) => call<Cart>(`/shop/cart/${id}`, { method: 'DELETE' }),
  checkout: (data: { billing: Billing; reference: string; note: string; accept_terms: boolean; waive_withdrawal: boolean; save_profile: boolean }) =>
    call<Order>('/shop/checkout', { method: 'POST', json: data }),
  orders: () => call<OrderSummary[]>('/shop/orders'),
  order: (number: string) => call<Order>(`/shop/orders/${encodeURIComponent(number)}`),
  stripe: (number: string) => call<{ url: string }>(`/shop/orders/${encodeURIComponent(number)}/stripe`, { method: 'POST' }),
  confirm: (number: string, session_id: string) => call<Order>(`/shop/orders/${encodeURIComponent(number)}/confirm`, { method: 'POST', json: { session_id } }),
  addProof: (number: string, form: FormData) => call<Order>(`/shop/orders/${encodeURIComponent(number)}/proofs`, { method: 'POST', body: form }),
  removeProof: (number: string, id: number) => call<Order>(`/shop/orders/${encodeURIComponent(number)}/proofs/${id}`, { method: 'DELETE' }),
  inquiry: (data: Record<string, unknown>) => call<{ ok: boolean }>('/shop/inquiries', { method: 'POST', json: data }),
  account: () => call<Billing>('/shop/account'),
  saveAccount: (b: Billing) => call<Billing>('/shop/account', { method: 'PUT', json: b }),
}

export const useShopConfig = () => useQuery({ queryKey: ['shop-config'], queryFn: shop.config, staleTime: 10 * 60_000 })
export const useCatalog = () => useQuery({ queryKey: ['catalog'], queryFn: shop.catalog, staleTime: 30 * 60_000 })
export const useCart = () => useQuery({ queryKey: ['cart'], queryFn: shop.cart, staleTime: 30_000 })

export function eur(cents: number | null | undefined) {
  if (cents == null) return '—'
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

export function pctLabel(m: Pick<Modifier, 'kind' | 'pct' | 'pct_max' | 'is_minimum'>) {
  if (m.kind === 'by_agreement' || m.pct == null) return 'auf Anfrage'
  const sign = m.kind === 'discount' ? '−' : '+'
  return `${m.is_minimum ? 'ab ' : ''}${sign}${m.pct.toLocaleString('de-DE')}${m.pct_max ? `–${m.pct_max.toLocaleString('de-DE')}` : ''} %`
}

export const EMPTY_BILLING: Billing = {
  first_name: '', last_name: '', organisation: '', street: '', postal_code: '', city: '', country: 'DE', vat_id: '', phone: '', email: '',
}

/** Quick picks for common uses; skipped when the medium/table isn't offered. */
export const PRESETS: { label: string; config: Omit<LicenseConfig, 'modifiers'> }[] = [
  { label: 'Website · 1 Jahr', config: { medium: 'ad_online', table: 'online', params: { duration: '1 Jahr', placement: 'Unterseite' } } },
  { label: 'Social Media · 1 Monat', config: { medium: 'ad_social_media', table: 'social_media', params: { duration: '1 Monat' } } },
  { label: 'Zeitschrift · ½ Seite', config: { medium: 'ed_magazine', table: 'print', params: { size: '1/2' } } },
  { label: 'Tageszeitung', config: { medium: 'ed_daily_newspaper', table: 'print', params: {} } },
  { label: 'Buch', config: { medium: 'ed_books', table: 'print', params: { exclusivity: 'nicht exklusiv' } } },
  { label: 'Online-Artikel', config: { medium: 'ed_online', table: 'online_press', params: { paywall: 'nicht kostenpflichtig' } } },
  { label: 'Ausstellung', config: { medium: 'ed_exhibitions', table: 'exhibition', params: {} } },
  { label: 'Kalender', config: { medium: 'hp_calendar', table: 'calendar', params: {} } },
]
